import React, { Component } from 'react';
import styled from 'styled-components';
import { changeLocation } from 'api/Location';
import { getRequest, batchRequest, postRequest } from '../../api/ApiService';
import { Loader } from 'react-loaders';
import Auth from '../../routes/Auth';
import orderBy from 'lodash/orderBy';

import Navigation from './Navigation';
import Pagination from './Pagination';
import CopiedMessage from './CopiedMessage';
import Page404 from './Page404';

import WelcomeStep1 from './welcome/Step1';
import TriviaIntro from './trivia/Intro';
import TriviaSingleChoice from './trivia/SingleChoice';
import TriviaMultipleChoice from './trivia/MultipleChoice';
import TriviaFormula1 from './trivia/Formula1';
import TriviaFormula2 from './trivia/Formula2';
import TriviaFinal from './trivia/Final';
import TeamsAndCompetitionsStep from './TeamsAndCompetitions';
import {
  CustomerPreferences,
  MachineOptions,
  DistributionCenters,
  ShareBuyback,
  ProductStrategy,
  NewMetricROE,
  NewMetricROA,
  NewMetricEPS,
  ConductResearch
} from './what-to-expect';
import NextStepsPage from './NextSteps';

import ConfettiExplosion from './ConfettiExplosion';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-self: stretch;

  .loader {
    display: inline-block;
    margin-top: 20px;
    margin-left: 20px;
  }

  .loader .loader-inner div {
    border-color: #36507e;
  }
`;

const LoaderWrapper = styled.div`
  background: #f5f6f7;
  padding: 40px;
  flex: 1;
`;

const ConfettiWrapper = styled.div`
  position: absolute;
  top: 59px;
  left: 475px;
  width: calc(100vw - 475px);
  height: calc(100vh - 139px);
  overflow: hidden;
`;

const ConfettiPosition = styled.div`
  position: absolute;
  top: 30%;
  left: 50%;
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: calc(30% + 59px);
  left: calc(50% + 130px);

  font-family: 'Mukta Vaani';
  font-style: normal;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  padding: 10px 20px;
  background: #FFBABA;
  border: 1px solid #D8000C;
  border-radius: 8px;
  z-index: 3000;
`;

class OnboardingPage extends Component {

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      pages: [{
        title: 'Welcome',
        alias: 'welcome',
        steps: [
          { component: WelcomeStep1, alias: '' },
        ],
      }],
      currentPage: -2,
      currentStep: -2,
      mode: 'basic', // basic | disabled | validate | loading
      showMessage: false,
      validateCallback: null,
      history: [],
      showConfetti: false,
      isFetchData: false,
      showIncorrectMessage: false,
      navigationLoaded: false,
    };
  }

  componentDidMount() {
    this.loadNavigation(() => {
      this.getCurrentPage();
    });
  }

  setFetchData = (isFetchData) => {
    this.setState({
      isFetchData,
    });
  }

  showConfetti = () => {
    this.setState({
      showConfetti: true,
    }, () => {
      setTimeout(() => {
        this.setState({
          showConfetti: false,
        })
      }, 5000);
    });
  }

  showIncorrectMessage = () => {
    this.setState({
      showIncorrectMessage: true,
    }, () => {
      setTimeout(() => {
        this.setState({
          showIncorrectMessage: false,
        })
      }, 5000);
    });
  }

  loadNavigation = (callback) => {
    const { location, roundInUrl, currentRound } = this.props;
    let round = roundInUrl;

    if (location.query.round) {
      round = parseInt(location.query.round, 10);

      if (isNaN(round)) {
        round = currentRound;
      }
    }

    batchRequest([
      `${process.env.REACT_APP_API_URL}v2/trivia/coursetrivias/`,
      `${process.env.REACT_APP_API_URL}static/debt_clients/`,
      `${process.env.REACT_APP_API_URL}v2/trivia/triviaresponses/`,
    ])
      .then(res => {
        const userId = Number(Auth.getUserId());
        const triviasRaw = res[0].body;
        const stage = triviasRaw.results.find(i => i.trivia.stage === 'Welcome');
        const triviaId = stage ? stage.id : 0;
        let questions = stage ? stage.trivia.triviaquestions : [];
        questions = orderBy(questions, ['list_order'], ['asc']);
        for (let i = 0; i < questions.length; i++) {
          questions[i].question.optionchoices = orderBy(questions[i].question.optionchoices, ['id'], ['asc']);
        }

        const debtClients = res[1].body;
        const debtClient1 = debtClients.find(i => i.id === 1);
        const debtClientValue1 = debtClient1 ? `${(Number(debtClient1.lt_debt_to_capital) * 100).toFixed(0)}%` : '';
        const debtClient2 = debtClients.find(i => i.id === 2);
        const debtClientValue2 = debtClient2 ? `${(Number(debtClient2.lt_debt_to_capital) * 100).toFixed(0)}%` : '';
        const debtClient3 = debtClients.find(i => i.id === 3);
        const debtClientValue3 = debtClient3 ? `${(Number(debtClient3.lt_debt_to_capital) * 100).toFixed(0)}%` : '';

        const triviaAnswersBody = res[2].body;
        const triviaAnswers = triviaAnswersBody.results;

        const triviaSteps = [];

        const trivias = triviasRaw.results;
        const trivia = trivias.find(i => (
          Number(i.course) === Number(Auth.getCourseId())
          && i.trivia.stage === 'Welcome'
        ));

        let counter = 0;
        for (const question of questions) {
          counter++;
          if (question.question.question_type === 'Single Choice') {
            const hasAnswer = triviaAnswers.find(i => (
              Number(i.user) === userId
              && Number(i.trivia_question) === question.id
              && Number(i.course_trivia) === triviaId
            ));

            triviaSteps.push({
              component: TriviaSingleChoice,
              alias: `question${counter}`,
              hasAnswer: !!hasAnswer,
              data: {
                num: counter,
                text: question.question.text,
                choices: question.question.optionchoices,
                questionId: question.id,
                triviaId,
                userId,
              },
            });
          } else if (question.question.question_type === 'Multiple Choice') {
            const hasAnswer = triviaAnswers.find(i => (
              Number(i.user) === userId
              && Number(i.trivia_question) === question.id
              && Number(i.course_trivia) === triviaId
            ));
            triviaSteps.push({
              component: TriviaMultipleChoice,
              alias: `question${counter}`,
              hasAnswer: !!hasAnswer,
              data: {
                num: counter,
                text: question.question.text,
                choices: question.question.optionchoices,
                questionId: question.id,
                triviaId,
                userId,
              },
            });
          } else if (
            question.question.question_type === 'Multiple Choice with Ordering'
            && [22, 38, 15].includes(question.question.id)
          ) {
            const hasAnswer = triviaAnswers.find(i => (
              Number(i.user) === userId
              && Number(i.trivia_question) === question.id
              && Number(i.course_trivia) === triviaId
            ));
            triviaSteps.push({
              component: TriviaFormula1,
              alias: `question${counter}`,
              hasAnswer: !!hasAnswer,
              data: {
                num: counter,
                text: question.question.text,
                choices: question.question.optionchoices,
                captions: question.question.id === 22
                  ? ['Assets', 'Liabilities', 'Equity']
                  : question.question.id === 38
                    ? ['Gross Profit Section', 'Operating Profit Section', 'Net Profit Section']
                    : [debtClientValue1, debtClientValue2, debtClientValue3],
                questionId: question.id,
                triviaId,
                userId,
              },
            });
          } else if (
            question.question.question_type === 'Multiple Choice with Ordering'
            && [20, 37, 35, 36, 34, 28].includes(question.question.id)
          ) {
            const hasAnswer = triviaAnswers.find(i => (
              Number(i.user) === userId
              && Number(i.trivia_question) === question.id
              && Number(i.course_trivia) === triviaId
            ));
            triviaSteps.push({
              component: TriviaFormula2,
              alias: `question${counter}`,
              hasAnswer: !!hasAnswer,
              data: {
                num: counter,
                text: question.question.text,
                choices: question.question.optionchoices,
                caption: question.question.id === 20
                  ? 'Market Share'
                  : [37, 35].includes(question.question.id)
                    ? 'Return on Assets'
                    : [36, 34].includes(question.question.id)
                      ? 'Return on Equity'
                      : 'Debt to Total Capital',
                questionId: question.id,
                triviaId,
                userId,
              },
            });
          }
        }

        const stepsWelcome = round === 1
          ? [
            { component: WelcomeStep1, alias: 'intro' },
          ]
          : [{ component: WelcomeStep1, alias: '' }];

        let tempPages = [
          {
            title: 'Welcome',
            alias: 'welcome',
            steps: stepsWelcome,
          }
        ];
        if (round === 1) {
          if (!!trivia) {
            tempPages.push({
              title: 'Trivia: Warm Up',
              alias: 'trivia',
              steps: [
                { component: TriviaIntro, alias: 'intro' },
                ...triviaSteps,
                { component: TriviaFinal, alias: 'done' },
              ]
            });
          }

          tempPages.push({
            title: 'Teams & Competitions',
            alias: 'teams-and-competitions',
            steps: [
              { component: TeamsAndCompetitionsStep, alias: '' },
            ],
          });
        }

        if (round > 3) {
          tempPages.push({
            title: 'What to Expect',
            alias: '',
            hasChilds: true,
            steps: [
              { alias: '' },
            ],
          }, {
            title: 'Customer Preferences',
            alias: 'customer-preferences',
            subpage: true,
            steps: [
              { component: CustomerPreferences, alias: '' },
            ],
          });
          if (round === 2) {
            tempPages.push({
              title: 'Product Strategy',
              alias: 'product-strategy',
              subpage: true,
              steps: [
                { component: ProductStrategy, alias: '' },
              ],
            }, {
              title: 'Machine Options',
              alias: 'machine-options',
              subpage: true,
              steps: [
                { component: MachineOptions, alias: '' },
              ],
            }, {
              title: 'Distribution Centers',
              alias: 'distribution-centers',
              subpage: true,
              steps: [
                { component: DistributionCenters, alias: '' },
              ],
            });
          }

          if (round === 3) {
            tempPages.push({
              title: 'Share Buyback',
              alias: 'share-buyback',
              subpage: true,
              steps: [
                { component: ShareBuyback, alias: '' },
              ],
            }, {
              title: 'New Metric: ROE',
              alias: 'new-metric-roe',
              subpage: true,
              steps: [
                { component: NewMetricROE, alias: '' },
              ],
            }, {
              title: 'New Metric: ROA',
              alias: 'new-metric-roa',
              subpage: true,
              steps: [
                { component: NewMetricROA, alias: '' },
              ],
            }, {
              title: 'Conduct Research',
              alias: 'conduct-research',
              subpage: true,
              steps: [
                { component: ConductResearch, alias: '' },
              ],
            });
          }

          if (round === 4) {
            tempPages.push({
              title: 'New Metric: EPS',
              alias: 'new-metric-eps',
              subpage: true,
              steps: [
                { component: NewMetricEPS, alias: '' },
              ],
            }, {
              title: 'Conduct Research',
              alias: 'conduct-research',
              subpage: true,
              steps: [
                { component: ConductResearch, alias: '' },
              ],
            });
          }
        }

        tempPages.push({
          title: 'Next Steps',
          alias: 'next-steps',
          steps: [
            { component: NextStepsPage, alias: '' },
          ]
        });

        this.setState({
          pages: tempPages,
          navigationLoaded: true,
        }, () => {
          if (callback) {
            callback();
          }
        });
      })
      .catch(err => {
        console.log(err);
      });
  }

  componentDidUpdate() {
    const { navigationLoaded } = this.state;

    if (navigationLoaded === true) {
      this.getCurrentPage();
    }
  }

  getLastNotFinishedPage = (trackedPages, pages) => {
    let prevPage = 0;
    let prevStep = 0;
    for (let pageNum = 0; pageNum < pages.length; pageNum++) {
      for (let stepNum = 0; stepNum < pages[pageNum].steps.length; stepNum++) {
        const pageAlias = pages[pageNum].alias;
        const stepAlias = pages[pageNum].steps[stepNum].alias;

        if (pageAlias === '' && stepAlias === '') {
          continue;
        }

        const foundPage = trackedPages.find(i => i[0] === pageAlias && i[1] === stepAlias);
        if (!foundPage) {
          return {
            page: prevPage,
            step: prevStep,
          };
        }

        prevPage = pageNum;
        prevStep = stepNum;
      }
    }

    return {
      page: prevPage,
      step: prevStep,
    };
  }

  get activeRound() {
    const { currentRound, roundInUrl } = this.props;

    return roundInUrl || currentRound;
  }

  getCurrentPage() {
    const { location } = this.props;
    const { pages } = this.state;
    const currentRound = this.activeRound;

    const pathes = location.pathname.substring(1).split('/');

    let currentPage = 0;
    let currentStep = 0;
    let doRedirect = true;
    if (pathes.length > 1) {
      currentPage = -1;
      currentStep = -1;
      doRedirect = false;

      const category = pages.findIndex(i => i.alias === pathes[1]);
      if (category > -1) {
        if (pathes.length === 3) {
          const step = pages[category].steps.findIndex(i => i.alias === pathes[2]);
          if (step > -1) {
            currentPage = category;
            currentStep = step;
          }
        } else {
          currentPage = category;
          currentStep = 0;
        }
      }

      if (
        currentPage !== this.state.currentPage ||
        currentStep !== this.state.currentStep
      ) {
        this.setState({
          loading: true,
          currentPage,
          currentStep,
        });

        const user_id = Number(Auth.getUserId());
        const round_number = Number(currentRound);
        const game_id = Number(Auth.getGameId());
        const course_id = Number(Auth.getCourseId());
        getRequest(`${process.env.REACT_APP_API_URL}v2/engagement/tracks/?game=${game_id}&course=${course_id}&user=${user_id}&round_number=${round_number}`)
          .then(res => {
            let history = res.body.results.filter(i => (
              i.page.startsWith(`${window.location.protocol}//${window.location.host}/onboarding/`)
            )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/onboarding/`, ''));

            this.setState({
              loading: false,
              history,
            }, () => {
              if (doRedirect) {
                const page = pages[currentPage].alias;
                const step = pages[currentPage].steps[currentStep].alias;

                changeLocation(
                  `/onboarding/${page}${step !== '' ? '/' + step : ''}?round=${currentRound}`
                );
              }
            });
          })
          .catch(err => { });
      }
    } else {
      if (this.state.loading !== true) {
        this.setState({
          loading: true,
        });

        const user_id = Number(Auth.getUserId());
        const round_number = Number(currentRound);
        const game_id = Number(Auth.getGameId());
        const course_id = Number(Auth.getCourseId());
        getRequest(`${process.env.REACT_APP_API_URL}v2/engagement/tracks/?game=${game_id}&course=${course_id}&user=${user_id}&round_number=${round_number}`)
          .then(res => {
            let history = res.body.results.filter(i => (
              i.page.startsWith(`${window.location.protocol}//${window.location.host}/onboarding/`)
            )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/onboarding/`, ''));
            const trackedPages = res.body.results.filter(i => (
              i.page.startsWith(`${window.location.protocol}//${window.location.host}/onboarding/`)
            )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/onboarding/`, '').split('/'))
              .map(i => i.length === 2 ? i : [i[0], '']);

            if (trackedPages.length > 0) {
              const foundPage = this.getLastNotFinishedPage(trackedPages, pages);
              if (foundPage) {
                currentPage = foundPage.page;
                currentStep = foundPage.step;
              }
            }

            if (doRedirect) {
              const page = pages[currentPage].alias;
              const step = pages[currentPage].steps[currentStep].alias;

              if (currentPage === 0 && currentStep === 0 && !history.includes(`${page}${step !== '' ? '/' + step : ''}`)) {
                postRequest(`${process.env.REACT_APP_API_URL}v2/engagement/tracks/`)
                  .send({
                    page: `${window.location.protocol}//${window.location.host}/onboarding/${page}${step !== '' ? '/' + step : ''}`,
                    user: Auth.getUserId(),
                    course: Auth.getCourseId(),
                    game: Auth.getGameId(),
                    round_number: currentRound,
                  })
                  .then((data) => {
                    history = [
                      ...history,
                      `${page}${step !== '' ? '/' + step : ''}`,
                    ];

                    this.setState({
                      history,
                    });
                  });
              }
              changeLocation(
                `/onboarding/${page}${step !== '' ? '/' + step : ''}?round=${currentRound}`
              );
            }

            this.setState({
              loading: false,
              currentPage,
              currentStep,
              history,
            });
          })
          .catch(err => {
            console.log(err);
          });
      }
    }
  }

  trackPage = (url, page, step) => {
    const currentRound = this.activeRound;

    const user_id = Number(Auth.getUserId());
    const round_number = Number(currentRound);
    const game_id = Number(Auth.getGameId());
    const course_id = Number(Auth.getCourseId());
    getRequest(`${process.env.REACT_APP_API_URL}v2/engagement/tracks/?game=${game_id}&course=${course_id}&user=${user_id}&round_number=${round_number}`)
      .then(res => {
        const current_page = `${window.location.protocol}//${window.location.host}${url}`;

        const history = res.body.results.filter(i => (
          i.page.startsWith(`${window.location.protocol}//${window.location.host}/onboarding/`)
        )).map(i => i.page.replace(`${window.location.protocol}//${window.location.host}/onboarding/`, ''));
        const trackedPage = res.body.results.find(i => (
          i.page === current_page
        ));

        changeLocation(`${url}?round=${currentRound}`);

        if (!trackedPage) {
          postRequest(`${process.env.REACT_APP_API_URL}v2/engagement/tracks/`)
            .send({
              page: current_page,
              user: user_id,
              course: course_id,
              game: game_id,
              round_number: round_number,
            })
            .then((data) => {
              this.setState({
                history: [
                  ...history,
                  `${page}${step !== '' ? '/' + step : ''}`,
                ],
              });
            });
        }
      })
      .catch(err => { });
  }

  goNext = e => {
    e.preventDefault();

    const { pages, currentPage, currentStep } = this.state;

    let nextPage = currentPage;
    let nextStep = currentStep + 1;
    if (nextStep > pages[nextPage].steps.length - 1) {
      nextPage = currentPage + 1;
      nextStep = 0;

      if (nextPage > pages.length - 1) {
        return;
      }
    }

    if (pages[nextPage].hasChilds) {
      nextPage += 1;
    }

    const page = pages[nextPage].alias;
    const step = pages[nextPage].steps[nextStep].alias;

    this.trackPage(
      `/onboarding/${page}${step !== '' ? '/' + step : ''}`,
      page,
      step,
    );

    this.setState({
      currentPage: nextPage,
      currentStep: nextStep,
      showIncorrectMessage: false,
    });
  }

  goPrev = e => {
    e.preventDefault();

    const { pages, currentPage, currentStep } = this.state;

    let prevPage = currentPage;
    let prevStep = currentStep - 1;
    if (prevStep < 0) {
      prevPage = currentPage - 1;
      if (prevPage < 0) {
        return;
      }

      prevStep = pages[prevPage].steps.length - 1;
    }

    if (pages[prevPage].hasChilds) {
      prevPage -= 1;
    }

    const page = pages[prevPage].alias;
    const step = pages[prevPage].steps[prevStep].alias;

    changeLocation(
      `/onboarding/${page}${step !== '' ? '/' + step : ''}?round=${this.activeRound}`
    );

    this.setState({
      currentPage: prevPage,
      currentStep: prevStep,
    });
  }

  onValidate = e => {
    e.preventDefault();

    const { validateCallback } = this.state;

    if (validateCallback) {
      validateCallback();
    }
  }

  get isFirstStep() {
    const { currentPage, currentStep } = this.state;

    return currentPage === 0 && currentStep === 0;
  }

  get isLastStep() {
    const { pages, currentPage, currentStep } = this.state;

    return (
      currentPage === pages.length - 1 &&
      currentStep === pages[pages.length - 1].steps.length - 1
    );
  }

  setMode = (mode, validateCallback = null) => {
    if (['basic', 'disabled', 'validate', 'loading'].includes(mode) && mode !== this.state.mode) {
      this.setState({
        mode,
        validateCallback,
      });
    }
  }

  showMessage = () => {
    this.setState({
      showMessage: true,
    }, () => {
      setTimeout(() => {
        this.setState({
          showMessage: false,
        });
      }, 5000);
    });
  }

  render() {
    const {
      currentPage,
      currentStep,
      pages,
      mode,
      showMessage,
      loading,
      history,
      showConfetti,
      isFetchData,
      showIncorrectMessage,
    } = this.state;
    const { rounds, roundInUrl } = this.props;
    const currentRound = this.activeRound;
    const ContentPage = loading
      ? null
      : currentPage > -1 && currentStep > -1
        ? pages[currentPage].steps[currentStep].component
        : Page404;
    const data = loading
      ? null
      : currentPage > -1 && currentStep > -1
        ? pages[currentPage].steps[currentStep].data || null
        : null;
    const pageAlias = loading
      ? null
      : currentPage > -1
        ? pages[currentPage].alias
        : null;

    return (
      <Wrapper>
        {currentPage > -1 && currentStep > -1 &&
          <Navigation
            pages={pages}
            currentPage={currentPage}
            history={history}
            currentRound={currentRound}
          />}
        {showConfetti && (
          <ConfettiWrapper>
            <ConfettiPosition>
              <ConfettiExplosion
                force={0.6}
                duration={3000}
                particleCount={100}
                height={1000}
                width={1000}
              />
            </ConfettiPosition>
          </ConfettiWrapper>
        )}
        {showIncorrectMessage && <ErrorMessage>This is incorrect!</ErrorMessage>}
        <Content>
          {ContentPage &&
            <ContentPage
              setMode={this.setMode}
              showMessage={this.showMessage}
              currentRound={currentRound}
              rounds={rounds}
              data={data}
              stage="welcome"
              showConfetti={this.showConfetti}
              setFetchData={this.setFetchData}
              isFetchData={isFetchData}
              showIncorrectMessage={this.showIncorrectMessage}
              roundInUrl={roundInUrl}
            />}
          {!ContentPage && (
            <LoaderWrapper>
              <Loader type="ball-scale-ripple" active />
            </LoaderWrapper>
          )}
          {showMessage && <CopiedMessage />}
          {currentPage > -1 && currentStep > -1 &&
            <Pagination
              currentStep={currentStep + 1}
              countOfSteps={pages[currentPage].steps.length}
              goNext={this.goNext}
              goPrev={this.goPrev}
              onValidate={this.onValidate}
              isFirstStep={this.isFirstStep}
              isLastStep={this.isLastStep}
              isNextDisabled={mode === 'disabled'}
              isValidationMode={mode === 'validate'}
              isLoadingMode={mode === 'loading'}
              isTriviaPage={pageAlias === 'trivia'}
            />}
        </Content>
      </Wrapper>
    );
  }

}

export default OnboardingPage;



// WEBPACK FOOTER //
// ./src/components/onboarding/OnboardingPage.js